import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  X, Loader2, CalendarDays, Users, Check, AlertTriangle, Ban, Phone,
} from 'lucide-react'
import { apiGet, cachedGet, postJson, patchJson } from '../../lib/api.js'
import { apiErrorText } from '../../lib/apiError.js'
import ConfirmDialog from '../ConfirmDialog.jsx'
import AudiencePicker from '../broadcast/AudiencePicker.jsx'
import ReachPanel from '../broadcast/ReachPanel.jsx'
import {
  hasAudience, audienceSummary, statusMeta, toneClass, OUTCOME_META,
  timeOnDay, hhmm, shiftWindowLabel, ROSTER_GAP_NOTE,
} from '../../lib/broadcast.js'
import {
  relabelBranch, fmtDayTH, shiftPayload, asList, isShiftRow,
} from '../../lib/doctorSchedule.js'
import { doctorCode, doctorFullName } from '../../lib/dentist.js'

/**
 * ขอเวร — create a request for one day, or open an existing one to pick a doctor.
 *
 * Two modes in one drawer because they are two views of one row: `dayKey` opens
 * the create form, `requestId` opens the responses.
 *
 * 🚨 THE ROSTER GAP IS THE IMPORTANT PART OF THIS FILE. `shift_request` is its
 * own table and its LIFECYCLE never writes `appointment` (owner's decision,
 * 2026-08-20) — picking a doctor does not silently roster them. But the manager
 * still has to close that gap, so after a pick this drawer offers an explicit
 * **เพิ่มเวร** button that performs the roster write through the roster's OWN
 * endpoint (POST /api/hr/schedule/doctor → Xano POST /appointment), which is
 * what fires `appointment_update` and puts the doctor on the booking calendar.
 *
 * ⚠️ It used to be a deep link carrying ?branch&date&doctor&start&end, described
 * as "prefilled". DoctorSchedule reads NO query params, so it prefilled nothing
 * and the button looked broken. Do not reintroduce that link without teaching
 * the target to read them.
 *
 * "Already rostered?" is answered by READING the roster for that branch+day, not
 * by a flag on the request. A flag would drift the moment someone added the
 * shift by hand; the read is self-correcting.
 */

const INPUT = 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-[13.5px] text-slate-800 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10'
const DEFAULT_WINDOW = { start: '10:00', end: '20:00' }

export default function ShiftRequestDrawer({
  dayKey, requestId, branches = [], defaultBranchId = '', onClose, onSaved,
}) {
  const creating = requestId == null
  return (
    <Shell title={creating ? 'ขอเวร' : 'คำขอเวร'} onClose={onClose}>
      {creating
        ? <CreateForm dayKey={dayKey} branches={branches} defaultBranchId={defaultBranchId} onClose={onClose} onSaved={onSaved} />
        : <RequestDetail id={requestId} branches={branches} onClose={onClose} onSaved={onSaved} />}
    </Shell>
  )
}

/* ── create ──────────────────────────────────────────────────────────────── */

function CreateForm({ dayKey, branches, defaultBranchId, onClose, onSaved }) {
  const [date, setDate] = useState(dayKey || '')
  const [branchId, setBranchId] = useState(defaultBranchId || '')
  const [start, setStart] = useState(DEFAULT_WINDOW.start)
  const [end, setEnd] = useState(DEFAULT_WINDOW.end)
  const [slots, setSlots] = useState(1)
  const [note, setNote] = useState('')
  const [target, setTarget] = useState({ target_all: false, target_spec: [], target_doctor: [] })
  const [doctors, setDoctors] = useState([])
  const [preview, setPreview] = useState(null)
  const [previewing, setPreviewing] = useState(false)
  const [previewErr, setPreviewErr] = useState('')
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')
  const [confirm, setConfirm] = useState(false)
  const seq = useRef(0)

  useEffect(() => {
    let alive = true
    cachedGet('/api/doctors', { slim: 1 })
      .then((d) => { if (alive) setDoctors(Array.isArray(d) ? d : (d?.items || [])) })
      .catch(() => {})
    return () => { alive = false }
  }, [])

  const ready = hasAudience(target)

  useEffect(() => {
    if (!ready) { setPreview(null); setPreviewErr(''); return }
    const mine = ++seq.current
    setPreviewing(true)
    const t = setTimeout(() => {
      postJson('/api/hr/broadcast/preview', target)
        .then((r) => { if (seq.current === mine) { setPreview(r); setPreviewErr('') } })
        .catch((e) => { if (seq.current === mine) { setPreview(null); setPreviewErr(apiErrorText(e, 'คำนวณผู้รับไม่สำเร็จ')) } })
        .finally(() => { if (seq.current === mine) setPreviewing(false) })
    }, 250)
    return () => clearTimeout(t)
  }, [target, ready])

  const windowOk = Boolean(date && start && end && end > start)
  const canSend = ready && windowOk && branchId && !saving

  async function send() {
    setConfirm(false)
    setSaving(true)
    setErr('')
    try {
      await postJson('/api/hr/shift-request', {
        branch_id: Number(branchId),
        date,
        start_date: timeOnDay(date, start),
        end_date: timeOnDay(date, end),
        slots: Number(slots) || 1,
        note: note.trim(),
        ...target,
      })
      onSaved()
    } catch (e) {
      setErr(apiErrorText(e, 'สร้างคำขอเวรไม่สำเร็จ'))
    } finally {
      setSaving(false)
    }
  }

  const reachable = Number(preview?.reachable || 0)

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[13px] font-medium text-slate-600 mb-1.5">วันที่</label>
          <input type="date" className={INPUT} value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
        <div>
          <label className="block text-[13px] font-medium text-slate-600 mb-1.5">สาขา</label>
          <select className={INPUT} value={branchId} onChange={(e) => setBranchId(e.target.value)}>
            <option value="">เลือกสาขา…</option>
            {branches.map((b) => <option key={b.id} value={b.id}>{relabelBranch(b.name)}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[13px] font-medium text-slate-600 mb-1.5">เวลาเริ่ม</label>
          <input type="time" className={INPUT} value={start} onChange={(e) => setStart(e.target.value)} />
        </div>
        <div>
          <label className="block text-[13px] font-medium text-slate-600 mb-1.5">เวลาสิ้นสุด</label>
          <input type="time" className={INPUT} value={end} onChange={(e) => setEnd(e.target.value)} />
        </div>
      </div>
      {date && start && end && end <= start && (
        <p className="text-[12.5px] text-rose-600">เวลาสิ้นสุดต้องอยู่หลังเวลาเริ่ม</p>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[13px] font-medium text-slate-600 mb-1.5">ต้องการหมอ (ท่าน)</label>
          <input type="number" min={1} max={20} className={INPUT} value={slots} onChange={(e) => setSlots(e.target.value)} />
        </div>
      </div>

      <div>
        <label className="block text-[13px] font-medium text-slate-600 mb-1.5">รายละเอียดเพิ่มเติม</label>
        <input className={INPUT} value={note} onChange={(e) => setNote(e.target.value)} placeholder="เช่น เคสเด็ก 3 ราย" />
      </div>

      <AudiencePicker value={target} onChange={setTarget} doctors={doctors} disabled={saving} />
      <ReachPanel preview={preview} loading={previewing} error={previewErr} />

      {err && <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2.5 text-[13px] text-rose-700">{err}</div>}

      <div className="flex items-center gap-2 pt-1">
        <button
          disabled={!canSend}
          onClick={() => setConfirm(true)}
          className="inline-flex items-center gap-1.5 rounded-xl bg-brand-600 px-4 py-2 text-[13.5px] font-medium text-white hover:bg-brand-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {saving ? <Loader2 size={15} className="animate-spin" /> : <CalendarDays size={15} strokeWidth={2} />}
          ส่งคำขอ
        </button>
        <button onClick={onClose} className="rounded-xl px-3 py-2 text-[13.5px] text-slate-500 hover:bg-slate-50 transition-colors">
          ยกเลิก
        </button>
      </div>

      {confirm && (
        <ConfirmDialog
          title="ส่งคำขอเวรนี้?"
          message={
            reachable > 0
              ? `${fmtDayTH(date)} ${start}–${end} · ส่งถึงคุณหมอ ${reachable} ท่านที่เข้าระบบได้`
              : 'ยังไม่มีคุณหมอที่เข้าระบบได้ในกลุ่มนี้ คำขอจะถูกบันทึกไว้แต่จะไม่มีใครเห็น'
          }
          confirmLabel="ส่งคำขอ"
          tone={reachable > 0 ? 'default' : 'danger'}
          onConfirm={send}
          onCancel={() => setConfirm(false)}
        />
      )}
    </div>
  )
}

/* ── detail + pick ───────────────────────────────────────────────────────── */

function RequestDetail({ id, branches, onClose, onSaved }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [chosen, setChosen] = useState(() => new Set())
  const [busy, setBusy] = useState(false)
  const [confirmPick, setConfirmPick] = useState(false)
  const [confirmCancel, setConfirmCancel] = useState(false)
  const [roster, setRoster] = useState(null)      // shift rows for this branch+day
  const [rostering, setRostering] = useState(0)   // doctor_id currently being written
  const [confirmRoster, setConfirmRoster] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const r = await apiGet(`/api/hr/shift-request/${id}`)
      setData(r)
      setErr('')
    } catch (e) {
      setErr(apiErrorText(e, 'โหลดคำขอเวรไม่สำเร็จ'))
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => { load() }, [load])

  const req = data?.shift_request

  /* Who is ALREADY on the roster for this branch+day. Read rather than stored:
   * a flag on the request would drift the moment someone added the shift by
   * hand, and this is the number the manager is actually asking about. */
  const loadRoster = useCallback(async () => {
    const day = String(req?.date || '').slice(0, 10)
    if (!req?.branch_id || !day) return
    try {
      const r = await apiGet('/api/hr/schedule/doctor', {
        branch_id: req.branch_id, start_date: day, end_date: day,
      })
      setRoster(asList(r).filter(isShiftRow))
    } catch {
      setRoster([])   // unknown, not "none" — the UI keeps offering the button
    }
  }, [req?.branch_id, req?.date])

  useEffect(() => { loadRoster() }, [loadRoster])

  const isRostered = useCallback(
    (doctorId) => (roster || []).some((r) => Number(r.doctor_id) === Number(doctorId)),
    [roster],
  )

  /* The roster write. Goes through the ROSTER's own endpoint, not a private one:
   * that is the call which fires appointment_update/{branch_id}, so the booking
   * screens see the doctor appear live. A parallel writer would be silent. */
  async function addShift(p) {
    setConfirmRoster(null)
    setRostering(p.doctor_id)
    setErr('')
    try {
      const built = shiftPayload({
        doctor_id: p.doctor_id,
        branch_id: req.branch_id,
        date: String(req.date).slice(0, 10),
        start: hhmm(req.start_date),
        end: hhmm(req.end_date),
        details: req.note || '',
      }, { doctorTitle: doctorCode(p) || doctorFullName(p) })
      if (built.error) throw new Error(built.error)
      await postJson('/api/hr/schedule/doctor', built.payload)
      await loadRoster()
    } catch (e) {
      setErr(apiErrorText(e, 'เพิ่มเวรไม่สำเร็จ'))
    } finally {
      setRostering(0)
    }
  }
  const responses = data?.responses || []
  const interested = responses.filter((r) => r.response === 'interested')
  const declined = responses.filter((r) => r.response === 'declined')
  const accepted = responses.filter((r) => r.outcome === 'accepted')
  const branchName = useMemo(() => {
    const b = branches.find((x) => Number(x.id) === Number(req?.branch_id))
    return b ? relabelBranch(b.name) : `สาขา #${req?.branch_id ?? '—'}`
  }, [branches, req])

  const slots = Number(req?.slots || 1)
  const canPick = req?.status === 'open' && interested.length > 0

  async function pick() {
    setConfirmPick(false)
    setBusy(true)
    try {
      await postJson(`/api/hr/shift-request/${id}/pick`, { doctor_ids: [...chosen] })
      await load()
      onSaved?.()
    } catch (e) {
      setErr(apiErrorText(e, 'เลือกคุณหมอไม่สำเร็จ'))
    } finally {
      setBusy(false)
    }
  }

  async function cancelRequest() {
    setConfirmCancel(false)
    setBusy(true)
    try {
      await patchJson(`/api/hr/shift-request/${id}`, { status: 'cancelled' })
      onSaved?.()
    } catch (e) {
      setErr(apiErrorText(e, 'ยกเลิกคำขอไม่สำเร็จ'))
    } finally {
      setBusy(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-[13.5px] text-slate-500 py-8 justify-center">
        <Loader2 size={16} className="animate-spin" /> กำลังโหลด…
      </div>
    )
  }
  if (err && !req) {
    return <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2.5 text-[13px] text-rose-700">{err}</div>
  }

  const meta = statusMeta('shift_request', req?.status)

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[15px] font-semibold text-slate-900">{fmtDayTH(String(req?.date || '').slice(0, 10))}</span>
          <span className={`rounded-md px-1.5 py-0.5 text-[11.5px] ring-1 ${toneClass(meta.tone)}`}>{meta.label}</span>
        </div>
        <p className="text-[13.5px] text-slate-700">{branchName} · {shiftWindowLabel(req)} · ต้องการ {slots} ท่าน</p>
        {req?.note && <p className="text-[13px] text-slate-500 mt-1">{req.note}</p>}
        <p className="text-[12px] text-slate-400 mt-2">
          {audienceSummary(req)} · ส่งถึง {req?.reach_login ?? 0} จาก {req?.reach_total ?? 0} ท่าน
          {req?.created_by_name ? ` · โดย ${req.created_by_name}` : ''}
        </p>
      </div>

      {/* 🚨 The gap, stated. A closed request whose doctor is not on the roster
          is the failure mode this whole panel exists to prevent. */}
      {req?.status === 'closed' && accepted.length > 0 && (() => {
        const pending = accepted.filter((a) => !isRostered(a.doctor_id))
        const done = accepted.filter((a) => isRostered(a.doctor_id))
        const allDone = pending.length === 0
        return (
          <div className={`rounded-xl border px-3 py-3 ${
            allDone ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'
          }`}>
            <div className="flex items-start gap-2">
              {allDone
                ? <Check size={15} strokeWidth={2} className="mt-0.5 shrink-0 text-emerald-600" />
                : <AlertTriangle size={15} strokeWidth={1.75} className="mt-0.5 shrink-0 text-amber-600" />}
              <div className="min-w-0 flex-1">
                <p className={`text-[13.5px] font-medium ${allDone ? 'text-emerald-900' : 'text-amber-900'}`}>
                  {allDone ? 'ลงตารางเวรเรียบร้อยแล้ว — คนไข้จองคุณหมอท่านนี้ได้' : ROSTER_GAP_NOTE}
                </p>
                {!allDone && (
                  <p className="text-[12.5px] text-amber-800/80 mt-0.5">
                    คำขอเวรกับตารางเวรแยกกัน — กดปุ่มด้านล่างเพื่อเพิ่มเวรลงตารางจริง
                  </p>
                )}

                <div className="flex flex-col gap-1.5 mt-2">
                  {accepted.map((a) => {
                    const name = doctorCode(a) || doctorFullName(a) || `หมอ #${a.doctor_id}`
                    const rostered = isRostered(a.doctor_id)
                    return (
                      <div key={a.doctor_id} className="flex items-center gap-2">
                        <span className="text-[12.5px] text-slate-700 truncate">{name}</span>
                        {rostered ? (
                          <span className="ml-auto inline-flex items-center gap-1 rounded-md bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200 px-1.5 py-0.5 text-[11.5px]">
                            <Check size={11} strokeWidth={2.5} /> ลงตารางเวรแล้ว
                          </span>
                        ) : (
                          <button
                            type="button"
                            disabled={rostering === a.doctor_id || roster === null}
                            onClick={() => setConfirmRoster(a)}
                            className="ml-auto inline-flex items-center gap-1 rounded-lg border border-amber-300 bg-white/80 px-2.5 py-1 text-[12.5px] font-medium text-amber-900 hover:bg-white disabled:opacity-50 transition-colors"
                          >
                            {rostering === a.doctor_id
                              ? <Loader2 size={12} className="animate-spin" />
                              : <CalendarDays size={12} strokeWidth={2} />}
                            เพิ่มเวรของ {name}
                          </button>
                        )}
                      </div>
                    )
                  })}
                </div>

                {done.length > 0 && !allDone && (
                  <p className="text-[11.5px] text-amber-800/70 mt-2">
                    เพิ่มแล้ว {done.length} จาก {accepted.length} ท่าน
                  </p>
                )}
              </div>
            </div>
          </div>
        )
      })()}

      {err && <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2.5 text-[13px] text-rose-700">{err}</div>}

      <ResponseGroup
        title="สนใจ"
        people={interested}
        selectable={canPick}
        chosen={chosen}
        max={slots}
        onToggle={(did) => setChosen((s) => {
          const n = new Set(s)
          if (n.has(did)) n.delete(did)
          else if (n.size < slots) n.add(did)
          return n
        })}
      />
      <ResponseGroup title="ไม่สะดวก" people={declined} tone="slate" />

      {responses.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-200 px-4 py-8 text-center text-[13px] text-slate-400">
          ยังไม่มีคุณหมอตอบกลับ
        </div>
      )}

      <div className="flex items-center gap-2 pt-1 border-t border-slate-100">
        {canPick && (
          <button
            disabled={busy || chosen.size === 0}
            onClick={() => setConfirmPick(true)}
            className="inline-flex items-center gap-1.5 rounded-xl bg-brand-600 px-4 py-2 text-[13.5px] font-medium text-white hover:bg-brand-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} strokeWidth={2} />}
            เลือก {chosen.size > 0 ? `${chosen.size} ท่าน` : 'คุณหมอ'}
          </button>
        )}
        {req?.status === 'open' && (
          <button
            disabled={busy}
            onClick={() => setConfirmCancel(true)}
            className="ml-auto inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-[13.5px] text-rose-600 hover:bg-rose-50 disabled:opacity-50 transition-colors"
          >
            <Ban size={14} strokeWidth={1.75} /> ยกเลิกคำขอ
          </button>
        )}
      </div>

      {confirmPick && (
        <ConfirmDialog
          title="ยืนยันการเลือก?"
          // Names the OTHER half explicitly. Everyone who put their hand up gets
          // an answer, and the manager should know that before pressing it.
          message={`คุณหมอที่เลือก ${chosen.size} ท่านจะได้รับแจ้ง “ได้รับเวรนี้”${
            interested.length - chosen.size > 0
              ? ` และอีก ${interested.length - chosen.size} ท่านจะได้รับแจ้ง “ไม่ได้รับเวรนี้”`
              : ''
          } · คำขอจะปิดรับทันที`}
          confirmLabel="ยืนยัน"
          onConfirm={pick}
          onCancel={() => setConfirmPick(false)}
        />
      )}

      {confirmRoster && (
        <ConfirmDialog
          title="เพิ่มเวรลงตารางจริง?"
          // Names every value being written. This is a real roster row on the
          // live booking calendar, not a note on the request.
          message={`${doctorCode(confirmRoster) || doctorFullName(confirmRoster)} · ${branchName} · ${fmtDayTH(String(req?.date || '').slice(0, 10))} ${shiftWindowLabel(req)} — คนไข้จะเริ่มจองคุณหมอท่านนี้ได้ทันที`}
          confirmLabel="เพิ่มเวร"
          onConfirm={() => addShift(confirmRoster)}
          onCancel={() => setConfirmRoster(null)}
        />
      )}

      {confirmCancel && (
        <ConfirmDialog
          title="ยกเลิกคำขอเวรนี้?"
          message={`${fmtDayTH(String(req?.date || '').slice(0, 10))} ${shiftWindowLabel(req)} · คุณหมอที่ตอบไว้แล้ว ${responses.length} ท่านจะไม่เห็นคำขอนี้อีก`}
          confirmLabel="ยกเลิกคำขอ"
          tone="danger"
          onConfirm={cancelRequest}
          onCancel={() => setConfirmCancel(false)}
        />
      )}
    </div>
  )
}

function ResponseGroup({ title, people, selectable = false, chosen, max, onToggle, tone = 'emerald' }) {
  if (!people.length) return null
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1.5 text-[12.5px] text-slate-500">
        <Users size={13} strokeWidth={1.75} />
        <span>{title}</span>
        <span className="text-slate-400">({people.length})</span>
        {selectable && <span className="ml-auto text-slate-400">เลือกได้ {max} ท่าน</span>}
      </div>
      <ul className="rounded-xl ring-1 ring-slate-200 divide-y divide-slate-100 bg-white overflow-hidden">
        {people.map((p) => {
          const isChosen = chosen?.has(p.doctor_id)
          const outcome = OUTCOME_META[p.outcome] || OUTCOME_META.none
          const row = (
            <>
              <div className="min-w-0 flex-1">
                <div className="text-[13.5px] text-slate-800 truncate">
                  {doctorCode(p) || doctorFullName(p) || `หมอ #${p.doctor_id}`}
                </div>
                {p.response_note && <div className="text-[12px] text-slate-500 truncate">{p.response_note}</div>}
              </div>
              {p.phone && (
                <a
                  href={`tel:${p.phone}`}
                  onClick={(e) => e.stopPropagation()}
                  className="shrink-0 inline-flex items-center gap-1 text-[12px] text-slate-400 hover:text-brand-600 transition-colors"
                >
                  <Phone size={12} strokeWidth={1.75} /> {p.phone}
                </a>
              )}
              {p.outcome !== 'none' && (
                <span className={`shrink-0 rounded-md px-1.5 py-0.5 text-[11.5px] ring-1 ${toneClass(outcome.tone)}`}>
                  {outcome.label}
                </span>
              )}
            </>
          )
          return selectable ? (
            <li key={p.doctor_id}>
              <button
                onClick={() => onToggle(p.doctor_id)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors ${
                  isChosen ? 'bg-brand-50' : 'hover:bg-slate-50'
                }`}
              >
                <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
                  isChosen ? 'bg-brand-600 border-brand-600' : 'border-slate-300'
                }`}>
                  {isChosen && <Check size={11} strokeWidth={3} className="text-white" />}
                </span>
                {row}
              </button>
            </li>
          ) : (
            <li key={p.doctor_id} className="flex items-center gap-2.5 px-3 py-2">{row}</li>
          )
        })}
      </ul>
    </div>
  )
}

/* ── shell ───────────────────────────────────────────────────────────────── */

function Shell({ title, children, onClose }) {
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose?.() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/25" onClick={onClose} />
      <aside className="absolute right-0 top-0 bottom-0 w-full max-w-[620px] bg-white shadow-xl flex flex-col">
        <header className="flex items-center gap-3 px-5 h-14 border-b border-slate-200 shrink-0">
          <h2 className="text-[15px] font-semibold text-slate-900 truncate">{title}</h2>
          <button onClick={onClose} className="ml-auto p-1.5 -mr-1.5 text-slate-400 hover:text-slate-700 transition-colors">
            <X size={18} strokeWidth={1.75} />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-5 py-5">{children}</div>
      </aside>
    </div>
  )
}
